/** TanStack Query wrappers for a task's elements of the invention. */

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryClient, UseMutationResult } from "@tanstack/react-query";
import type {
  DeclaredSource,
  FallbackKind,
  InventionElementsResponse,
  InventionNamesResponse,
} from "@patent-studio/api-types";
import {
  addElement,
  addFallback,
  amendElement,
  amendFallback,
  confirmElement,
  listElements,
  listNames,
  listPassages,
  nameElement,
  nameFallback,
  removeElement,
  relateElement,
  removeFallback,
  setSubject,
  settleInvention,
  rerunExtraction,
  startExtraction,
  unrelateElement,
} from "@/services/invention";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";
import {
  claimStrategyKey,
  inventionElementsKey,
  inventionNamesKey,
  passagesKey,
  thesisGateScopes,
} from "./query-keys";

/** What an amendment needs: which element, and what it should now say. */
export interface AmendVariables {
  elementId: string;
  text: string;
}

/** What an addition needs. The wording alone, for now. */
export interface AddVariables {
  text: string;
  declaredSource: DeclaredSource;
  /** The element the new one follows, or null for the start of the sequence. */
  afterElementId: string | null;
}

/** What adding a rung needs: the element, the rung's kind and words, the class it sits under if any, and where it came from. */
export interface AddFallbackVariables {
  elementId: string;
  kind: FallbackKind;
  text: string;
  parentId: string | null;
  declaredSource: DeclaredSource;
}

/** What rewording a rung needs. */
export interface AmendFallbackVariables {
  elementId: string;
  fallbackId: string;
  text: string;
}

/** Which rung, on which element. */
export interface FallbackRef {
  elementId: string;
  fallbackId: string;
}

/** What naming an element needs: which element, and the term claims will use for it. */
export interface NameVariables {
  elementId: string;
  term: string;
}

/** What naming a rung needs. */
export interface NameFallbackVariables {
  elementId: string;
  fallbackId: string;
  term: string;
}

/** What a relation needs: the element, the one it attaches to, and the words of its text that say how. */
export interface RelateVariables {
  elementId: string;
  targetId: string;
  words: string;
}

const NO_DRAFT = "No draft selected";

/**
 * Discard the served gates after a write on this page.
 *
 * An invention write can unsettle a position, since a position's yes is given
 * to the wording of the elements it rests on, and the commit gate reads whether
 * every position is settled. The gate is keyed by the files and the positions
 * alone. An edit here moves nothing in that key, so without this the rail goes
 * on marking Novelty done off an answer the server would no longer give. Every write discards it, including the few that only move a
 * stamp: telling those apart here would cost one request and would make the
 * next write that does move the wording a silent regression.
 */
function lapseServedGates(queryClient: QueryClient, taskId: string | null) {
  if (taskId === null) return;
  for (const queryKey of thesisGateScopes(taskId)) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/**
 * What every write that answers with the whole set does with that answer: it
 * replaces the set the page holds, and the gates it may have moved are dropped.
 */
function holdingSet(
  queryClient: QueryClient,
  key: readonly unknown[],
  taskId: string | null,
) {
  return (served: InventionElementsResponse) => {
    queryClient.setQueryData(key, served);
    lapseServedGates(queryClient, taskId);
    // A term is served where it sits in its words, so a write that moves the
    // words moves where the term is drawn.
    if (taskId !== null)
      void queryClient.invalidateQueries({
        queryKey: inventionNamesKey(taskId),
      });
  };
}

/** The mutation that ran most recently, whichever it was. */
function latestOf<T extends { submittedAt: number }>(
  mutations: readonly T[],
): T {
  return mutations.reduce((latest, mutation) =>
    mutation.submittedAt > latest.submittedAt ? mutation : latest,
  );
}

/**
 * The elements of the open draft's invention, in the order the server serves them.
 *
 * Read once. While a derivation is running the page follows it over the task's
 * stream (``useInventionExtraction``), which patches this cache as the status moves
 * and asks for the set again once it settles.
 */
export function useInventionElements(taskId: string | null) {
  return useQuery({
    queryKey: inventionElementsKey(taskId ?? ""),
    queryFn: () => listElements(taskId ?? ""),
    enabled: taskId !== null,
  });
}

/**
 * The term of each element and rung. Read beside the set rather than inside it:
 * a term is not part of what the attorney confirms, so naming one answers with
 * the names and leaves the set, and its confirmation, as they were.
 */
export function useInventionNames(taskId: string | null) {
  return useQuery({
    queryKey: inventionNamesKey(taskId ?? ""),
    queryFn: () => listNames(taskId ?? ""),
    enabled: taskId !== null,
  });
}

/**
 * The passages one element cites in one file, as options rather than a hook,
 * because the drawer reads one such set per document it walks and the number
 * of documents is the element's to decide. With no element there is nothing
 * to ask, and the options say so rather than every caller inventing an id.
 */
export function passagesQuery(
  taskId: string,
  fileId: string,
  elementId: string | null,
) {
  return queryOptions({
    queryKey: passagesKey(taskId, fileId, elementId),
    queryFn: () => listPassages(taskId, fileId, elementId ?? ""),
    enabled: elementId !== null,
  });
}

export interface InventionWrites {
  add: UseMutationResult<InventionElementsResponse, Error, AddVariables>;
  amend: UseMutationResult<InventionElementsResponse, Error, AmendVariables>;
  confirm: UseMutationResult<InventionElementsResponse, Error, string>;
  remove: UseMutationResult<void, Error, string>;
  addFallback: UseMutationResult<
    InventionElementsResponse,
    Error,
    AddFallbackVariables
  >;
  amendFallback: UseMutationResult<
    InventionElementsResponse,
    Error,
    AmendFallbackVariables
  >;
  removeFallback: UseMutationResult<
    InventionElementsResponse,
    Error,
    FallbackRef
  >;
  nameElement: UseMutationResult<InventionNamesResponse, Error, NameVariables>;
  nameFallback: UseMutationResult<
    InventionNamesResponse,
    Error,
    NameFallbackVariables
  >;
  subject: UseMutationResult<InventionElementsResponse, Error, string>;
  relate: UseMutationResult<InventionElementsResponse, Error, RelateVariables>;
  unrelate: UseMutationResult<InventionElementsResponse, Error, string>;
  /** Any of the writes is in flight. */
  busy: boolean;
  /**
   * The failure of the write that ran last, or null. A mutation keeps its error
   * until it runs again, so reading them in a fixed order would leave a refused
   * add reported over every later write that succeeded.
   */
  lastError: Error | null;
}

type WithDraft = <T>(run: (id: string) => Promise<T>) => Promise<T>;

/**
 * The three writes on an element's ladder. Each answers with the whole set: a
 * removed class lifts its members, and the settle lapses, so the list the page
 * draws is the answer.
 */
function useLadderWrites(
  withDraft: WithDraft,
  held: (served: InventionElementsResponse) => void,
): Pick<InventionWrites, "addFallback" | "amendFallback" | "removeFallback"> {
  const addFallbackWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionFallbackAdd,
    mutationFn: (variables: AddFallbackVariables) =>
      withDraft((id) =>
        addFallback(id, variables.elementId, {
          kind: variables.kind,
          text: variables.text,
          parent_id: variables.parentId,
          declared_source: variables.declaredSource,
        }),
      ),
    onSuccess: held,
  });
  const amendFallbackWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionFallbackAmend,
    mutationFn: (variables: AmendFallbackVariables) =>
      withDraft((id) =>
        amendFallback(id, variables.elementId, variables.fallbackId, {
          text: variables.text,
        }),
      ),
    onSuccess: held,
  });
  const removeFallbackWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionFallbackRemove,
    mutationFn: (ref: FallbackRef) =>
      withDraft((id) => removeFallback(id, ref.elementId, ref.fallbackId)),
    onSuccess: held,
  });
  return {
    addFallback: addFallbackWrite,
    amendFallback: amendFallbackWrite,
    removeFallback: removeFallbackWrite,
  };
}

/**
 * The writes of a term and of the subject. A term answers with the names, which
 * replace the ones the page holds; the subject answers with the set, since the
 * confirmation covers it.
 */
function useNamingWrites(
  withDraft: WithDraft,
  taskId: string | null,
  held: (served: InventionElementsResponse) => void,
): Pick<InventionWrites, "nameElement" | "nameFallback" | "subject"> {
  const queryClient = useQueryClient();
  // Claim Strategy reads its terms and lead-ins from the names, so a name
  // written here moves what it serves.
  const holdingNames = async (served: InventionNamesResponse) => {
    const key = inventionNamesKey(taskId ?? "");
    // A read in flight, such as the one a reworded element starts, was sent
    // before this write and would land after it with the term as it was; it
    // is stopped first.
    await queryClient.cancelQueries({ queryKey: key });
    queryClient.setQueryData(key, served);
    if (taskId !== null)
      void queryClient.invalidateQueries({
        queryKey: claimStrategyKey(taskId),
      });
  };
  const nameElementWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementName,
    mutationFn: (variables: NameVariables) =>
      withDraft((id) =>
        nameElement(id, variables.elementId, { term: variables.term }),
      ),
    onSuccess: holdingNames,
  });
  const nameFallbackWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionFallbackName,
    mutationFn: (variables: NameFallbackVariables) =>
      withDraft((id) =>
        nameFallback(id, variables.elementId, variables.fallbackId, {
          term: variables.term,
        }),
      ),
    onSuccess: holdingNames,
  });
  const subjectWrite = useMutation({
    mutationKey: MUTATION_KEYS.inventionSubject,
    mutationFn: (subject: string) =>
      withDraft((id) => setSubject(id, { subject })),
    onSuccess: held,
  });
  return {
    nameElement: nameElementWrite,
    nameFallback: nameFallbackWrite,
    subject: subjectWrite,
  };
}

/** The writes of a relation. Each answers with the whole set: the confirmation covers the relation. */
function useRelationWrites(
  withDraft: WithDraft,
  held: (served: InventionElementsResponse) => void,
): Pick<InventionWrites, "relate" | "unrelate"> {
  const relate = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementRelate,
    mutationFn: (variables: RelateVariables) =>
      withDraft((id) =>
        relateElement(id, variables.elementId, {
          target_id: variables.targetId,
          words: variables.words,
        }),
      ),
    onSuccess: held,
  });
  const unrelate = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementUnrelate,
    mutationFn: (elementId: string) =>
      withDraft((id) => unrelateElement(id, elementId)),
    onSuccess: held,
  });
  return { relate, unrelate };
}

/**
 * The four writes open on an element.
 *
 * Each of the three that answer with a body write that answer straight into the
 * cache rather than invalidating. The server sends the whole set from every one
 * of them, precisely because confirming an element renumbers the ones after it,
 * so a refetch would be asking for what is already in hand and would leave the
 * attorney reading a stale list until it landed.
 *
 * The removal is the exception. It answers no content, so there is nothing to
 * write and the list is read again.
 */
export function useInventionWrites(taskId: string | null): InventionWrites {
  const queryClient = useQueryClient();
  const key = inventionElementsKey(taskId ?? "");

  const held = holdingSet(queryClient, key, taskId);

  const withDraft = <T>(run: (id: string) => Promise<T>) =>
    taskId === null ? Promise.reject(new Error(NO_DRAFT)) : run(taskId);

  const add = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementAdd,
    mutationFn: (variables: AddVariables) =>
      withDraft((id) =>
        addElement(id, {
          text: variables.text,
          declared_source: variables.declaredSource,
          placement: { after_element_id: variables.afterElementId },
        }),
      ),
    onSuccess: held,
  });

  const amend = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementAmend,
    mutationFn: (variables: AmendVariables) =>
      withDraft((id) =>
        amendElement(id, variables.elementId, { text: variables.text }),
      ),
    onSuccess: held,
  });

  const confirm = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementConfirm,
    mutationFn: (elementId: string) =>
      withDraft((id) => confirmElement(id, elementId)),
    onSuccess: held,
  });

  const remove = useMutation({
    mutationKey: MUTATION_KEYS.inventionElementRemove,
    mutationFn: (elementId: string) =>
      withDraft((id) => removeElement(id, elementId)),
    onSuccess: async () => {
      lapseServedGates(queryClient, taskId);
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const ladder = useLadderWrites(withDraft, held);

  const naming = useNamingWrites(withDraft, taskId, held);

  const relations = useRelationWrites(withDraft, held);

  const all = [
    add,
    amend,
    confirm,
    remove,
    ladder.addFallback,
    ladder.amendFallback,
    ladder.removeFallback,
    naming.nameElement,
    naming.nameFallback,
    naming.subject,
    relations.relate,
    relations.unrelate,
  ];
  return {
    add,
    amend,
    confirm,
    remove,
    ...ladder,
    ...naming,
    ...relations,
    busy: all.some((mutation) => mutation.isPending),
    lastError: latestOf(all).error,
  };
}

/**
 * Start the one derivation pass for the open draft.
 *
 * Fired from Intake's Continue, which navigates without waiting for it, so Invention has
 * usually issued its own read of the elements before this answers. The answer is invalidated
 * rather than written into the cache for exactly that reason: writing it risks an in-flight
 * read landing afterwards with the pre-start state and stopping the poll before it began.
 */
export function useStartExtraction(taskId: string | null) {
  return useDerivation(taskId, MUTATION_KEYS.inventionStartExtraction, (id) =>
    startExtraction(id),
  );
}

/**
 * Read the elements again from the files the matter holds now: the stale-elements banner's
 * Re-run. Invalidated as Continue's start is, so the page polls the pass it started.
 */
export function useRerunExtraction(taskId: string | null) {
  return useDerivation(taskId, MUTATION_KEYS.inventionRerunExtraction, (id) =>
    rerunExtraction(id),
  );
}

function useDerivation(
  taskId: string | null,
  mutationKey: readonly string[],
  ask: (taskId: string) => Promise<InventionElementsResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: () =>
      taskId === null ? Promise.reject(new Error(NO_DRAFT)) : ask(taskId),
    onSuccess: async () => {
      if (taskId !== null) {
        lapseServedGates(queryClient, taskId);
        await queryClient.invalidateQueries({
          queryKey: inventionElementsKey(taskId),
        });
      }
    },
  });
}

/**
 * Confirm the elements. The answer replaces the
 * set the page holds, and the gate it carries is what turns the footer. A
 * refusal means the server's gate and the page's disagree, so the set is read
 * again rather than left saying the page may be confirmed.
 */
export function useSettleInvention(
  taskId: string | null,
): UseMutationResult<InventionElementsResponse, Error, void> {
  const queryClient = useQueryClient();
  const key = inventionElementsKey(taskId ?? "");
  return useMutation({
    mutationKey: MUTATION_KEYS.inventionSettle,
    mutationFn: () =>
      taskId === null
        ? Promise.reject(new Error(NO_DRAFT))
        : settleInvention(taskId),
    onSuccess: holdingSet(queryClient, key, taskId),
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
